import { useCallback, useEffect, useRef, useState } from 'react'
import { AxiosError } from 'axios'
import Toast from 'components/Toast/Toast'
import { getWebsiteMetadata } from 'api/websiteMetadata'

type WebsiteMetadataState = {
  hasWebsite: boolean
  domainUrl: string | null
  lastPublishedAt: string | null
}

const initialState: WebsiteMetadataState = {
  hasWebsite: false,
  domainUrl: null,
  lastPublishedAt: null,
}

const useWebsiteMetadata = (organizationId: string) => {
  const [metadata, setMetadata] = useState<WebsiteMetadataState>(initialState)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const isMounted = useRef(true)

  const fetchWebsiteMetadata = useCallback(async () => {
    try {
      setLoading(true)
      const response = await getWebsiteMetadata(organizationId)

      const attributes = response?.data?.attributes

      if (attributes?.domain_url) {
        if (isMounted.current) {
          setMetadata({
            hasWebsite: true,
            domainUrl: attributes.domain_url,
            lastPublishedAt: attributes.last_published_at || null,
          })
          setError(null)
        }
      } else {
        if (isMounted.current) {
          setMetadata(initialState)
          setError('No domain_url found in response.')
        }
      }
    } catch (err: unknown) {

      const axiosError = err as AxiosError<{ errors?: string }>
      const status = axiosError.response?.status
      const errors = axiosError.response?.data?.errors

      if (isMounted.current) {
        setError('Failed to fetch website metadata')

        if (status !== 302 && errors && errors !== '') {
          Toast.error(
            {
              message: `Failed to fetch website metadata. Details: ${errors}`,
            },
            { autoClose: 3000 }
          )
        }
      }
    } finally {
      if (isMounted.current) {
        setLoading(false)
      }
    }
  }, [organizationId])

  useEffect(() => {
    isMounted.current = true
    if (organizationId) {
      fetchWebsiteMetadata()
    }

    return () => {
      isMounted.current = false
    }
  }, [organizationId, fetchWebsiteMetadata])

  return {
    metadata,
    loading,
    error,
    refetch: fetchWebsiteMetadata,
  }
}

export default useWebsiteMetadata
